import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { FadeUp, RevealMaskScroll } from "../components/Reveal";
import { useSiteContent } from "../context/SiteContentContext";

const Q = "?auto=compress&cs=tinysrgb&w=1400";
const IMG = {
  bride1: "https://images.pexels.com/photos/38526712/pexels-photo-38526712.jpeg" + Q,
  craft1: "https://images.pexels.com/photos/13206031/pexels-photo-13206031.jpeg" + Q,
  jewel2: "https://images.pexels.com/photos/35395104/pexels-photo-35395104.jpeg" + Q,
};

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const site = useSiteContent();
  const t = (k, f) => site.text(k, f);
  const m = (k, f) => site.media(k, f);

  return (
    <div data-testid="about-page">
      <section ref={ref} className="relative h-[85vh] overflow-hidden">
        <motion.img style={{ y, scale: 1.1 }} src={m("about_hero", IMG.bride1)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/45" />
        <div className="relative z-10 h-full flex flex-col justify-end px-5 md:px-10 pb-20 max-w-[1600px] mx-auto text-ivory">
          <p className="overline text-gold mb-5">{t("about_kicker", "Our Story")}</p>
          <h1 className="font-serif text-6xl md:text-9xl tracking-tight leading-none max-w-4xl">
            <RevealMaskScroll>{t("about_title_1", "The house of")}</RevealMaskScroll>
            <RevealMaskScroll delay={0.1} className="italic text-gold">{t("about_title_2", "AMRAIYA")}</RevealMaskScroll>
          </h1>
        </div>
      </section>

      <section className="px-5 md:px-10 py-24 md:py-32 max-w-[900px] mx-auto text-center">
        <FadeUp>
          <p className="font-serif text-3xl md:text-5xl leading-snug tracking-tight">
            {t("about_lead", "Amraiya — a warm glow of amber light — is where our garments live. Not quite tradition, not quite modernity, but the luminous space between.")}
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="mt-10 text-ink-soft leading-relaxed max-w-2xl mx-auto">
            {t("about_body", "Founded on a reverence for Indian craft, Amraiya works with karigars across Banaras, Lucknow and Jaipur to create pieces that are made slowly, deliberately, and to last generations. Every silhouette is an heirloom in the making.")}
          </p>
        </FadeUp>
      </section>

      <section className="grid md:grid-cols-2">
        {[[m("about_atelier", IMG.craft1), t("about_tile1", "The atelier")], [m("about_detail", IMG.jewel2), t("about_tile2", "The detail")]].map(([src, label], i) => (
          <FadeUp key={i}>
            <div className="relative aspect-[4/3] overflow-hidden group">
              <img src={src} alt={label} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
              <span className="absolute bottom-6 left-6 font-serif text-3xl text-ivory">{label}</span>
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
            </div>
          </FadeUp>
        ))}
      </section>

      <section className="bg-ink text-ivory py-24 md:py-32 text-center">
        <div className="px-5 md:px-10 max-w-[1000px] mx-auto grid grid-cols-3 gap-8">
          {[
            [t("about_stat1_num", "12+"), t("about_stat1_label", "Years of craft")],
            [t("about_stat2_num", "40+"), t("about_stat2_label", "Master karigars")],
            [t("about_stat3_num", "100%"), t("about_stat3_label", "Made to order")],
          ].map(([n, l], i) => (
            <FadeUp key={i} delay={i * 0.1}>
              <p className="font-serif text-5xl md:text-7xl text-gold">{n}</p>
              <p className="overline text-white/50 mt-3">{l}</p>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.3}>
          <Link to="/shop" className="btn-ink inline-block mt-16 px-10 py-4 overline bg-ivory text-ink hover:bg-gold hover:text-ink">
            {t("about_cta", "Discover the collection")}
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}
